import { useState, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SMS } from 'domains/content/types';
import useAssessments from 'hooks/queries/useAssessments';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

export enum WarningType {
    MISSING_TAG = 'missingTag',
    MISSING_ASSESSMENT = 'missingAssessment',
    INVALID_ASSESSMENT = 'invalidAssessment',
}

type UseAssessmentValidationProps = {
    content: string;
    combinedSmsData: Partial<SMS>;
    isAssessmentEnabled: boolean;
    handleSaveAndExit: (status?: ContentStatus) => void;
    setSmsData: (data: Partial<SMS>) => void;
    setIsAssessmentDialogVisible: (isVisible: boolean) => void;
};

const useAssessmentValidation = ({
    content,
    combinedSmsData,
    isAssessmentEnabled,
    handleSaveAndExit,
    setSmsData,
    setIsAssessmentDialogVisible,
}: UseAssessmentValidationProps) => {
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [warningType, setWarningType] = useState<WarningType>(null);
    const { contentDraft: hasContentDraft } = useFlags();

    const { data: assessmentsData } = useAssessments({
        reactQueryOptions: { enabled: isAssessmentEnabled },
    });

    // Validate assessment configuration before saving
    const validateAndSaveExit = useCallback(
        (status?: ContentStatus) => {
            const hasAssessmentSelected = Boolean(combinedSmsData?.assessmentId);
            const hasAssessmentTag = content.includes('{{assessment_url}}');

            const validations = [
                // Check if selected assessment still exists
                {
                    shouldCheck: () => hasAssessmentSelected && assessmentsData,
                    isInvalid: () =>
                        !assessmentsData.data.some((assessment) => assessment.id === combinedSmsData?.assessmentId),
                    warningType: WarningType.INVALID_ASSESSMENT,
                    message: 'Selected assessment no longer exists. Please choose another.',
                },
                // Check for assessment selected but no tag
                {
                    shouldCheck: () => true, // Always check this rule
                    isInvalid: () => hasAssessmentSelected && !hasAssessmentTag,
                    warningType: WarningType.MISSING_TAG,
                    message: 'Assessment selected but no merge tags have been added in the content',
                },
                // Check for tag present but no assessment selected
                {
                    shouldCheck: () => true, // Always check this rule
                    isInvalid: () => !hasAssessmentSelected && hasAssessmentTag,
                    warningType: WarningType.MISSING_ASSESSMENT,
                    message: 'Select assessment to support added merge tags in the content',
                },
            ];

            // Find the first validation that fails
            const failedValidation = validations.find(
                (validation) => validation.shouldCheck() && validation.isInvalid()
            );

            if (failedValidation) {
                setWarningType(failedValidation.warningType);
                setWarningMessage(failedValidation.message);
                return;
            }

            // Call handleSaveAndExit with status only if hasContentDraft is enabled, defaulting to DRAFT if not provided
            if (hasContentDraft) {
                handleSaveAndExit(status ?? ContentStatus.DRAFT);
            } else {
                handleSaveAndExit();
            }
        },
        [combinedSmsData, assessmentsData, content, handleSaveAndExit, hasContentDraft]
    );

    // Reset warning state
    const handleWarningClose = useCallback(() => {
        setWarningMessage(null);
        setWarningType(null);
    }, []);

    // Remove assessment
    const handleRemoveAssessment = useCallback(() => {
        setSmsData({
            assessmentId: '',
            assessmentName: '',
            assessmentConsentRequired: true,
        });
        handleWarningClose();
    }, [setSmsData, handleWarningClose]);

    // Show assessment selection dialog
    const handleSelectAssessment = useCallback(() => {
        setIsAssessmentDialogVisible(true);
        handleWarningClose();
    }, [setIsAssessmentDialogVisible, handleWarningClose]);

    return {
        warningMessage,
        warningType,
        validateAndSaveExit,
        handleWarningClose,
        handleRemoveAssessment,
        handleSelectAssessment,
    };
};

export default useAssessmentValidation;
